/**
 * Two-column layout: preview + form (mirrors sectional ConfiguratorLayout visually).
 */

import { useRef, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  BARRIER_BOOM_DEFAULT_MM,
  DEFAULT_BARRIER_FINISH,
  DEFAULT_BARRIER_MODEL,
  DEFAULT_BARRIER_SUPPORT,
  DEFAULT_HIGH_SPEED_FRAME,
  DEFAULT_HIGH_SPEED_GLAZING,
  DEFAULT_INDUSTRIAL_MOTIF,
  DEFAULT_INDUSTRIAL_SURFACE,
  DEFAULT_ROLLER_CASSETTE,
  DEFAULT_SWING_FRAME,
  DEFAULT_SWING_GLAZING,
  DEFAULT_SWING_PATTERN,
  type AccessCategoryId,
  type AccessColorOption,
  type AccessPhotoCatalogStored,
  type AccessRogerSeriesRule,
} from "@shared/access-configurator";
import AccessPreview, { type AccessPreviewState } from "./AccessPreview";
import AccessForm, { type AccessFormValues } from "./AccessForm";

interface AccessConfiguratorLayoutProps {
  categoryId: AccessCategoryId;
  colors: AccessColorOption[];
  photoCatalog: AccessPhotoCatalogStored;
  rogerRules?: AccessRogerSeriesRule[];
  token?: string;
  embedMode?: boolean;
  onSubmit: (values: AccessFormValues) => Promise<void>;
  submitting?: boolean;
}

export default function AccessConfiguratorLayout({
  categoryId,
  colors,
  photoCatalog,
  rogerRules,
  token,
  embedMode = false,
  onSubmit,
  submitting,
}: AccessConfiguratorLayoutProps) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<AccessPreviewState>(() => initialPreview(categoryId, colors));

  useEffect(() => {
    setPreview(initialPreview(categoryId, colors));
  }, [categoryId, colors]);

  const onPreviewChange = useCallback((patch: Partial<AccessPreviewState>) => {
    setPreview((p) => ({ ...p, ...patch }));
  }, []);

  const scrollRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const [stickyScale, setStickyScale] = useState(1);
  const scrollTopWhenStuckRef = useRef<number | null>(null);
  const SCROLL_FOR_MAX_SHRINK = 220;
  const MIN_SCALE = 0.7;
  const FADE_HEIGHT_PX = 32;
  const WHITE_STRIP_BELOW_DOOR_PX = 2;
  const WHITE_ABOVE_STRIP_PX = 8;
  const TOTAL_BOTTOM_ZONE_PX = FADE_HEIGHT_PX + WHITE_STRIP_BELOW_DOOR_PX;
  const DOOR_AREA_HEIGHT_PX = 280 - TOTAL_BOTTOM_ZONE_PX;

  const updateStickyScale = useCallback(() => {
    const scrollEl = scrollRef.current;
    const stickyEl = stickyRef.current;
    if (!scrollEl || !stickyEl) return;
    const stickyRect = stickyEl.getBoundingClientRect();
    const containerRect = scrollEl.getBoundingClientRect();
    const isStuck = stickyRect.top <= containerRect.top + 2;
    if (isStuck) {
      if (scrollTopWhenStuckRef.current === null) {
        scrollTopWhenStuckRef.current = scrollEl.scrollTop;
      }
      const extraScroll = scrollEl.scrollTop - scrollTopWhenStuckRef.current;
      const t = Math.min(1, extraScroll / SCROLL_FOR_MAX_SHRINK);
      setStickyScale(1 - (1 - MIN_SCALE) * t);
    } else {
      scrollTopWhenStuckRef.current = null;
      setStickyScale(1);
    }
  }, []);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    scrollEl.addEventListener("scroll", updateStickyScale, { passive: true });
    return () => scrollEl.removeEventListener("scroll", updateStickyScale);
  }, [updateStickyScale]);

  return (
    <div
      className={
        embedMode
          ? "access-configurator-layout configurator-layout flex flex-col flex-1 min-h-0 w-full"
          : "access-configurator-layout configurator-layout flex flex-col min-h-[100dvh] h-full min-h-0 w-full"
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_453px] flex-1 min-h-0 w-full">
        <div className="hidden lg:flex order-2 lg:order-1 min-h-0 items-center justify-center overflow-hidden bg-white">
          <AccessPreview
            state={preview}
            photoCatalog={photoCatalog}
            className={
              categoryId === "loading_dock" ? "w-full h-full min-h-0" : "w-full max-w-2xl"
            }
          />
        </div>

        <div
          ref={scrollRef}
          className={`order-1 lg:order-2 w-full flex flex-col min-h-0 overflow-y-auto border-l-2 border-border bg-background ${
            embedMode ? "h-full" : "max-h-full lg:max-h-[100dvh]"
          }`}
          style={{ boxShadow: "inset 4px 0 12px -4px rgba(0,0,0,0.08)" }}
        >
          <p
            className="shrink-0 text-xs text-muted-foreground leading-snug"
            style={{ padding: "12px 25px 0 25px" }}
          >
            {t("configurator.disclaimer")}
          </p>

          <header className="shrink-0" style={{ padding: "20px 25px 0 25px" }}>
            <div className="flex items-center gap-3 mb-[11px]">
              <span className="configurator-deco-line" />
              <p
                className="text-[15px] uppercase tracking-normal"
                style={{
                  fontFamily: "Roboto, Arial, Helvetica, sans-serif",
                  color: "var(--configurator-form-text)",
                }}
              >
                {t("accessConfigurator.subtitle")}
              </p>
            </div>
            <h1
              style={{
                fontSize: "48px",
                fontFamily: "'Saira Condensed', 'Sarabun', Arial, Helvetica, sans-serif",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              <span className="block" style={{ color: "var(--configurator-form-text)" }}>
                {t("configurator.titleLine1")}
              </span>
              <span className="block" style={{ color: "var(--configurator-warm-accent)" }}>
                {t("configurator.titleLine2")}
              </span>
            </h1>
          </header>

          <div
            ref={stickyRef}
            className="lg:hidden shrink-0 sticky top-0 z-30 w-full overflow-hidden relative"
            style={{
              height: `${Math.round(DOOR_AREA_HEIGHT_PX * stickyScale + TOTAL_BOTTOM_ZONE_PX)}px`,
              transition: "height 150ms ease-out",
              background: `linear-gradient(to bottom, #ffffff 0%, #ffffff calc(100% - ${TOTAL_BOTTOM_ZONE_PX}px), #ffffff calc(100% - ${WHITE_ABOVE_STRIP_PX}px), transparent calc(100% - ${WHITE_STRIP_BELOW_DOOR_PX}px), #ffffff calc(100% - ${WHITE_STRIP_BELOW_DOOR_PX}px), #ffffff 100%)`,
              boxShadow: "0 4px 20px -4px rgba(0,0,0,0.08)",
            }}
          >
            <div
              className="w-full flex items-center justify-center pt-[2px]"
              style={{
                height: `${DOOR_AREA_HEIGHT_PX}px`,
                transform: `scale(${stickyScale})`,
                transformOrigin: "top center",
                transition: "transform 150ms ease-out",
              }}
            >
              <AccessPreview
                state={preview}
                photoCatalog={photoCatalog}
                className={
                  categoryId === "loading_dock" ? "w-full h-full min-h-0" : "w-full max-w-2xl"
                }
              />
            </div>
          </div>

          <div className="flex-1" style={{ padding: "4px 25px 32px 25px" }}>
            <AccessForm
              categoryId={categoryId}
              colors={colors}
              photoCatalog={photoCatalog}
              rogerRules={rogerRules}
              preview={preview}
              onPreviewChange={onPreviewChange}
              token={token}
              onSubmit={onSubmit}
              submitting={submitting}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function initialPreview(categoryId: AccessCategoryId, colors: AccessColorOption[]): AccessPreviewState {
  return {
    categoryId,
    gateVariant:
      categoryId === "swing" ? "double_leaf" : categoryId === "residential_garage" ? "sectional" : "standard",
    colorHex: colors[0]?.colorHex ?? "#F6F6F6",
    colorCode: colors[0]?.code ?? "RAL 9016",
    visualVariant: "a",
    motif: DEFAULT_INDUSTRIAL_MOTIF,
    surface: DEFAULT_INDUSTRIAL_SURFACE,
    cassette: DEFAULT_ROLLER_CASSETTE,
    swingPattern: DEFAULT_SWING_PATTERN,
    swingFrame: DEFAULT_SWING_FRAME,
    swingGlazing: DEFAULT_SWING_GLAZING,
    highSpeedFrame: DEFAULT_HIGH_SPEED_FRAME,
    highSpeedGlazing: DEFAULT_HIGH_SPEED_GLAZING,
    barrierModel: DEFAULT_BARRIER_MODEL,
    barrierFinish: DEFAULT_BARRIER_FINISH,
    barrierLed: false,
    barrierSkirt: false,
    barrierSupport: DEFAULT_BARRIER_SUPPORT,
    lengthMm: categoryId === "barrier" ? BARRIER_BOOM_DEFAULT_MM : undefined,
    dockLeveller: false,
    dockLevellerType: "advise",
    dockSeal: false,
    dockSealType: "advise",
    dockBumpers: false,
    dockDoor: false,
  };
}
